import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { progressReportAPI } from '../../utils/api';
import { ProgressReport } from '../../types/progressReport';
import {
  MdArrowBack,
  MdTrendingUp,
  MdSchool,
  MdLightbulb,
  MdCheckCircle,
  MdWarning,
  MdPsychology,
} from 'react-icons/md';

const ArrowBack = MdArrowBack as any;
const TrendingUp = MdTrendingUp as any;
const School = MdSchool as any;
const Lightbulb = MdLightbulb as any;
const CheckCircle = MdCheckCircle as any;
const Warning = MdWarning as any;
const Psychology = MdPsychology as any;

export default function ProgressReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ProgressReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      if (!reportId) return;

      try {
        setLoading(true);
        const data = await progressReportAPI.getReportById(reportId);
        setReport(data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching progress report:', err);
        setError(err.message || 'Failed to load progress report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-400';
    if (confidence >= 0.6) return 'text-yellow-400';
    return 'text-orange-400';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.8) return 'Strong';
    if (confidence >= 0.6) return 'Good';
    return 'Developing';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (error || !report) {
    return (
      <Layout>
        <div className="min-h-screen bg-background p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-900/20 border border-red-600 rounded-lg p-6 text-center">
              <p className="text-red-200 mb-4">{error || 'Report not found'}</p>
              <button
                onClick={() => navigate('/progress-reports')}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition"
              >
                Back to Reports
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <button
            onClick={() => navigate('/progress-reports')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition"
          >
            <ArrowBack size={20} />
            Back to Reports
          </button>

          <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-8 mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">Progress Report</h1>
            <p className="text-gray-400 mb-4">
              {formatDate(report.date_range_start)} - {formatDate(report.date_range_end)}
            </p>
            <p className="text-lg text-gray-300">{report.summary}</p>
          </div>

          {/* Concepts Mastered */}
          {report.concepts_mastered.length > 0 && (
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle size={28} className="text-green-400" />
                <h2 className="text-2xl font-bold text-white">Concepts Mastered</h2>
              </div>
              <div className="space-y-4">
                {report.concepts_mastered.map((concept, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-800/50 rounded-lg p-4 flex justify-between items-start"
                  >
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {concept.concept}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {concept.subject} • Lesson {concept.lesson_number}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${getConfidenceColor(concept.confidence)}`}>
                        {getConfidenceLabel(concept.confidence)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {Math.round(concept.confidence * 100)}% confidence
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strengths and Areas for Improvement */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Strengths */}
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp size={28} className="text-green-400" />
                <h2 className="text-2xl font-bold text-white">Strengths</h2>
              </div>
              <ul className="space-y-3">
                {report.strengths.map((strength, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-green-400 mt-1">✓</span>
                    <span className="text-gray-300">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas for Improvement */}
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Warning size={28} className="text-yellow-400" />
                <h2 className="text-2xl font-bold text-white">Areas for Improvement</h2>
              </div>
              <ul className="space-y-3">
                {report.areas_for_improvement.map((area, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-yellow-400 mt-1">→</span>
                    <span className="text-gray-300">{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Notable Moments */}
          {report.notable_moments.length > 0 && (
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <Lightbulb size={28} className="text-yellow-400" />
                <h2 className="text-2xl font-bold text-white">Notable Moments</h2>
              </div>
              <div className="space-y-4">
                {report.notable_moments.map((moment, idx) => (
                  <div key={idx} className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-yellow-400 text-2xl">💡</span>
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-2">{moment.moment}</h3>
                        {moment.description && (
                          <p className="text-gray-400 text-sm">{moment.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">Lesson {moment.lesson_number}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subject Breakdown */}
          {Object.keys(report.subject_breakdown).length > 0 && (
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <School size={28} className="text-blue-400" />
                <h2 className="text-2xl font-bold text-white">Subject Breakdown</h2>
              </div>
              <div className="space-y-6">
                {Object.entries(report.subject_breakdown).map(([subject, data]) => (
                  <div key={subject} className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg font-semibold text-white">{subject}</h3>
                      <div className="text-right">
                        <p className={`text-sm font-semibold ${getConfidenceColor(data.overall_mastery_level)}`}>
                          {Math.round(data.overall_mastery_level * 100)}% Mastery
                        </p>
                        {data.progress_trajectory && (
                          <p className="text-xs text-gray-500 capitalize">{data.progress_trajectory}</p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">
                      {data.number_of_lessons} lesson{data.number_of_lessons !== 1 ? 's' : ''} completed
                    </p>
                    <p className="text-xs text-gray-500 mb-2">Topics covered:</p>
                    <div className="flex flex-wrap gap-2">
                      {(data.key_topics_covered || []).map((topic, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Learning Style */}
          {Object.keys(report.learning_style).length > 0 && (
            <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Psychology size={28} className="text-purple-400" />
                <h2 className="text-2xl font-bold text-white">Learning Style Insights</h2>
              </div>
              <div className="space-y-3">
                {Object.entries(report.learning_style).map(([key, value]) => (
                  <div key={key} className="flex items-start gap-3">
                    <span className="text-purple-400 font-semibold capitalize min-w-[120px]">
                      {key.replace(/_/g, ' ')}:
                    </span>
                    <span className="text-gray-300">
                      {Array.isArray(value) ? value.join(', ') : value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
